'use client';
import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Target, AlertTriangle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const COUNTY_PERFORMANCE = [
  { county: 'Montserrado', assessed: 4200000, collected: 3780000, parcels: 1842, compliance: 90, gap: 420000 },
  { county: 'Nimba', assessed: 1850000, collected: 1295000, parcels: 623, compliance: 70, gap: 555000 },
  { county: 'Bong', assessed: 980000, collected: 784000, parcels: 412, compliance: 80, gap: 196000 },
  { county: 'Lofa', assessed: 720000, collected: 432000, parcels: 298, compliance: 60, gap: 288000 },
  { county: 'Grand Bassa', assessed: 1560000, collected: 1404000, parcels: 387, compliance: 90, gap: 156000 },
  { county: 'Margibi', assessed: 890000, collected: 623000, parcels: 334, compliance: 70, gap: 267000 },
  { county: 'Grand Gedeh', assessed: 540000, collected: 432000, parcels: 198, compliance: 80, gap: 108000 },
  { county: 'Maryland', assessed: 680000, collected: 544000, parcels: 215, compliance: 80, gap: 136000 },
  { county: 'Sinoe', assessed: 420000, collected: 252000, parcels: 142, compliance: 60, gap: 168000 },
  { county: 'Grand Kru', assessed: 280000, collected: 196000, parcels: 98, compliance: 70, gap: 84000 },
  { county: 'River Cess', assessed: 190000, collected: 114000, parcels: 67, compliance: 60, gap: 76000 },
  { county: 'Gbarpolu', assessed: 210000, collected: 147000, parcels: 72, compliance: 70, gap: 63000 },
  { county: 'Grand Cape Mount', assessed: 320000, collected: 256000, parcels: 108, compliance: 80, gap: 64000 },
  { county: 'River Gee', assessed: 175000, collected: 105000, parcels: 58, compliance: 60, gap: 70000 },
  { county: 'Bomi', assessed: 245000, collected: 196000, parcels: 87, compliance: 80, gap: 49000 },
];

const getComplianceColor = (rate: number) => {
  if (rate >= 85) return '#34d399';
  if (rate >= 70) return '#fbbf24';
  return '#f87171';
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-3 rounded-lg text-xs" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
        <p className="font-bold mb-1" style={{ color: 'var(--dark-text)' }}>{label}</p>
        {payload.map((p: any) => (
          <p key={p.name} style={{ color: p.color }}>
            {p.name}: ${(p.value / 1000).toFixed(0)}K
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function CountyPerformanceReportPanel() {
  const [sortBy, setSortBy] = useState<'compliance' | 'gap' | 'collected'>('compliance');
  const [showTop, setShowTop] = useState(8);

  const sorted = [...COUNTY_PERFORMANCE].sort((a, b) => {
    if (sortBy === 'compliance') return b.compliance - a.compliance;
    if (sortBy === 'gap') return b.gap - a.gap;
    return b.collected - a.collected;
  }).slice(0, showTop);

  const totalAssessed = COUNTY_PERFORMANCE.reduce((s, c) => s + c.assessed, 0);
  const totalCollected = COUNTY_PERFORMANCE.reduce((s, c) => s + c.collected, 0);
  const totalGap = totalAssessed - totalCollected;
  const avgCompliance = Math.round(COUNTY_PERFORMANCE.reduce((s, c) => s + c.compliance, 0) / COUNTY_PERFORMANCE.length);

  const formatM = (val: number) => `$${(val / 1000000).toFixed(2)}M`;
  const formatK = (val: number) => `$${(val / 1000).toFixed(0)}K`;

  return (
    <div className="space-y-4">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Assessed', value: formatM(totalAssessed), color: 'var(--accent)', icon: Target },
          { label: 'Total Collected', value: formatM(totalCollected), color: '#34d399', icon: TrendingUp },
          { label: 'Revenue Gap', value: formatM(totalGap), color: '#f87171', icon: TrendingDown },
          { label: 'Avg Compliance', value: `${avgCompliance}%`, color: '#fbbf24', icon: AlertTriangle },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} style={{ color: s.color }} />
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
              </div>
              <p className="text-xl font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Collections vs Assessed Value by County</p>
          <div className="flex items-center gap-1.5">
            {(['compliance', 'gap', 'collected'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className="px-2.5 py-1 rounded-full text-xs font-medium transition-all capitalize"
                style={{
                  backgroundColor: sortBy === s ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                  color: sortBy === s ? 'var(--accent)' : 'var(--dark-muted)',
                  border: `1px solid ${sortBy === s ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={sorted} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="county" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false}
              tickFormatter={(v) => v.length > 8 ? v.slice(0, 8) + '…' : v} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false}
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="assessed" name="Assessed" fill="rgba(212,160,23,0.3)" radius={[3, 3, 0, 0]} />
            <Bar dataKey="collected" name="Collected" radius={[3, 3, 0, 0]}>
              {sorted.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getComplianceColor(entry.compliance)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* County table */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--dark-border)' }}>
          <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>County-Level Breakdown</p>
          <button
            onClick={() => setShowTop(showTop === 8 ? 15 : 8)}
            className="text-xs px-2.5 py-1 rounded-full transition-all"
            style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
          >
            {showTop === 8 ? 'Show All 15' : 'Show Top 8'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                {['County', 'Parcels', 'Assessed', 'Collected', 'Gap', 'Compliance Rate'].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const compColor = getComplianceColor(c.compliance);
                return (
                  <tr key={c.county} className="transition-colors hover:bg-white/3"
                    style={{ borderBottom: '1px solid var(--dark-border)' }}>
                    <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--dark-text)' }}>{c.county}</td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: 'var(--dark-muted)' }}>{c.parcels.toLocaleString()}</td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: 'var(--accent)' }}>{formatK(c.assessed)}</td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: '#34d399' }}>{formatK(c.collected)}</td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: '#f87171' }}>{formatK(c.gap)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 rounded-full h-1.5" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                          <div className="h-1.5 rounded-full" style={{ width: `${c.compliance}%`, backgroundColor: compColor }} />
                        </div>
                        <span className="font-bold font-mono-data w-8 text-right" style={{ color: compColor }}>{c.compliance}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
